import React, { useState } from 'react';
import { University } from '../../types';
import OverviewTab from './tabs/OverviewTab';
import RequirementsTab from './tabs/RequirementsTab';
import StatisticsTab from './tabs/StatisticsTab';
import TuitionTab from './tabs/TuitionTab';
import AcademicTab from './tabs/AcademicTab';
import PostCollegeTab from './tabs/PostCollegeTab';
import './UniversityModal.css';

interface UniversityModalProps {
  university: University | null;
  isOpen: boolean;
  onClose: () => void;
}

const UniversityModal: React.FC<UniversityModalProps> = ({
  university,
  isOpen,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !university) {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
  };

  // Get status badge info
  const getStatusBadge = () => {
    // Hardcoded for now as requested
    return {
      text: 'Reach School',
      className: 'bg-orange-100 text-orange-800'
    };
  };

  const statusBadge = getStatusBadge();

  return (
    <div 
      className="modal-overlay"
      onClick={handleBackdropClick}
    >
      <div className="modal-container">
        {/* Close Button */}
        <button 
          className="modal-close-btn"
          onClick={onClose}
        >
          <i className="fas fa-times text-xl"></i>
        </button>

        {/* Left Sidebar with Tabs */}
        <div className="modal-sidebar">
          <div className="university-header">
            {/* University logo placeholder */}
            <div className="w-16 h-16 rounded-lg mb-4 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <div className="text-center">
                <div className="text-gray-400 dark:text-gray-500 text-xs mb-1">
                  <i className="far fa-image text-lg"></i>
                </div>
                <div className="text-gray-400 dark:text-gray-500 text-xs font-medium">No Image</div>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{university.name}</h2>
            <p className="text-gray-600 text-sm">{university.location}</p>
            <div className="flex items-center mt-2">
              <span className={`px-3 py-1.5 rounded-full font-semibold text-sm ${statusBadge.className}`}>
                {statusBadge.text}
              </span>
            </div>
          </div>

          <nav className="tab-navigation">
            <button 
              className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => handleTabClick('overview')}
            >
              <i className="fas fa-info-circle mr-3"></i>
              Overview
            </button>
            <button 
              className={`tab-btn ${activeTab === 'requirements' ? 'active' : ''}`}
              onClick={() => handleTabClick('requirements')}
            >
              <i className="fas fa-clipboard-check mr-3"></i>
              Application Requirements
            </button>
            <button 
              className={`tab-btn ${activeTab === 'statistics' ? 'active' : ''}`}
              onClick={() => handleTabClick('statistics')}
            >
              <i className="fas fa-chart-bar mr-3"></i>
              Admission Statistics
            </button>
            <button 
              className={`tab-btn ${activeTab === 'tuition' ? 'active' : ''}`}
              onClick={() => handleTabClick('tuition')}
            >
              <i className="fas fa-dollar-sign mr-3"></i>
              Tuition & Costs
            </button>
            <button 
              className={`tab-btn ${activeTab === 'academic' ? 'active' : ''}`}
              onClick={() => handleTabClick('academic')}
            >
              <i className="fas fa-graduation-cap mr-3"></i>
              Academic Life
            </button>
            <button 
              className={`tab-btn ${activeTab === 'postgraduate' ? 'active' : ''}`}
              onClick={() => handleTabClick('postgraduate')}
            >
              <i className="fas fa-briefcase mr-3"></i>
              Post-College Life
            </button>
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="modal-content" style={{ width: '100%', maxWidth: 'none', flex: 1 }}>
          <div style={{ width: '100%', maxWidth: 'none' }}>
            {activeTab === 'overview' && <OverviewTab university={university} onTabChange={handleTabClick} />}
            {activeTab === 'requirements' && <RequirementsTab university={university} />}
            {activeTab === 'statistics' && <StatisticsTab university={university} />}
            {activeTab === 'tuition' && <TuitionTab university={university} />}
            {activeTab === 'academic' && <AcademicTab university={university} />}
            {activeTab === 'postgraduate' && <PostCollegeTab university={university} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UniversityModal;